<template>
  <div class="wce">
    <div class="wce-row">
      <label class="wce-label">Name</label>
      <input v-model.trim="draft.name" class="wce-input wce-mono" placeholder="NEW_COLUMN" />
    </div>
    <div class="wce-row">
      <label class="wce-label">Function</label>
      <select :value="draft.fn" class="wce-input" @change="setFn($event.target.value)">
        <option v-for="(spec, fn) in catalog" :key="fn" :value="fn">{{ spec.label }}</option>
      </select>
    </div>

    <div v-for="field in fields" :key="field.key" class="wce-row wce-row--top">
      <label class="wce-label">{{ field.label }}</label>
      <div class="wce-field">
        <select v-if="field.type === 'column'" v-model="draft.args[field.key]" class="wce-input wce-mono">
          <option v-if="field.optional" :value="undefined">(none)</option>
          <option v-for="c in columns" :key="c" :value="c">{{ c }}</option>
        </select>

        <select v-else-if="field.type === 'choice'" v-model="draft.args[field.key]" class="wce-input">
          <option v-for="o in field.options" :key="o" :value="o">{{ o }}</option>
        </select>

        <input v-else-if="field.type === 'int'" v-model.number="draft.args[field.key]" type="number" class="wce-input" :min="field.min" :max="field.max" />

        <input v-else-if="field.type === 'text'" v-model="draft.args[field.key]" class="wce-input wce-mono" :placeholder="field.placeholder" />

        <div v-else-if="field.type === 'columns'" class="wce-chips">
          <label v-for="c in columns" :key="c" class="wce-chip">
            <input type="checkbox" :checked="(draft.args[field.key] || []).includes(c)" @change="toggleIn(field.key, c)" />
            <span class="wce-mono">{{ c }}</span>
          </label>
        </div>

        <template v-else-if="field.type === 'operand' || field.type === 'operand_or_today'">
          <OperandInput v-model="draft.args[field.key]" :columns="columns" :allow-today="field.type === 'operand_or_today'" :optional="field.optional" />
        </template>

        <div v-else-if="field.type === 'operands'" class="wce-list">
          <div v-for="(op, i) in draft.args[field.key]" :key="i" class="wce-list-row">
            <OperandInput v-model="draft.args[field.key][i]" :columns="columns" />
            <button v-if="draft.args[field.key].length > 2" class="wce-del" title="Remove" @click="draft.args[field.key].splice(i, 1)">×</button>
          </div>
          <button v-if="draft.args[field.key].length < 6" class="wce-add" @click="draft.args[field.key].push({ col: columns[0] })">+ Add</button>
        </div>

        <div v-else-if="field.type === 'branches'" class="wce-list">
          <div v-for="(b, i) in draft.args.when" :key="i" class="wce-branch">
            <div class="wce-branch-cond">
              <span class="wce-when">when</span>
              <select v-model="b.column" class="wce-input wce-mono">
                <option v-for="c in columns" :key="c" :value="c">{{ c }}</option>
              </select>
              <select v-model="b.op" class="wce-input wce-op">
                <option v-for="op in ops" :key="op" :value="op">{{ op }}</option>
              </select>
              <input v-if="!nullOp(b.op)" :value="displayValue(b.value)" class="wce-input" :placeholder="listOp(b.op) ? 'a, b, c' : 'value'" @input="b.value = parseValue($event.target.value, listOp(b.op))" />
            </div>
            <div class="wce-branch-then">
              <span class="wce-when">then</span>
              <OperandInput v-model="b.then" :columns="columns" />
              <button v-if="draft.args.when.length > 1" class="wce-del" title="Remove" @click="draft.args.when.splice(i, 1)">×</button>
            </div>
          </div>
          <button v-if="draft.args.when.length < 12" class="wce-add" @click="draft.args.when.push(newBranch())">+ Add condition</button>
        </div>
      </div>
    </div>

    <p v-if="error" class="wce-error">{{ error }}</p>
    <div class="wce-actions">
      <button class="wce-cancel" @click="$emit('cancel')">Cancel</button>
      <button class="wce-save" @click="save">Save column</button>
    </div>
  </div>
</template>

<script>
import OperandInput from './WorkflowCalcOperand.vue';

const UNITS = ['day', 'week', 'month', 'quarter', 'year'];
const OPS = ['=', '!=', '>', '>=', '<', '<=', 'LIKE', 'NOT LIKE', 'IN', 'NOT IN', 'IS NULL', 'IS NOT NULL'];
const IDENT = /^[A-Za-z_][A-Za-z0-9_]*$/;
const col = (key, label, extra = {}) => ({ key, label, type: 'column', ...extra });
const operand = (key, label, extra = {}) => ({ key, label, type: 'operand', ...extra });

const CATALOG = {
  case: { label: 'If / then (case)', fields: [{ key: 'when', label: 'Conditions', type: 'branches' }, operand('else', 'Otherwise', { optional: true })] },
  coalesce: { label: 'First non-empty (coalesce)', fields: [{ key: 'parts', label: 'Values', type: 'operands' }] },
  datediff: { label: 'Time between dates', fields: [{ key: 'unit', label: 'Unit', type: 'choice', options: UNITS }, operand('start', 'From'), { key: 'end', label: 'To', type: 'operand_or_today' }] },
  date_trunc: { label: 'Round date down', fields: [{ key: 'unit', label: 'Unit', type: 'choice', options: UNITS }, col('column', 'Date')] },
  year: { label: 'Year of date', fields: [col('column', 'Date')] },
  month: { label: 'Month of date', fields: [col('column', 'Date')] },
  per_key: { label: 'Total per key (level of detail)', fields: [{ key: 'agg', label: 'Aggregate', type: 'choice', options: ['count', 'count_distinct', 'sum', 'min', 'max', 'avg'] }, col('column', 'Of', { optional: true }), { key: 'by', label: 'For each', type: 'columns' }] },
  add: { label: 'Add', fields: [operand('left', 'Value'), operand('right', 'Plus')] },
  sub: { label: 'Subtract', fields: [operand('left', 'Value'), operand('right', 'Minus')] },
  mul: { label: 'Multiply', fields: [operand('left', 'Value'), operand('right', 'Times')] },
  div: { label: 'Divide', fields: [operand('left', 'Value'), operand('right', 'Divided by')] },
  round: { label: 'Round number', fields: [operand('value', 'Value'), { key: 'digits', label: 'Decimals', type: 'int', min: 0, max: 6 }] },
  cast: { label: 'Convert type', fields: [col('column', 'Column'), { key: 'to', label: 'To', type: 'choice', options: ['number', 'date', 'text'] }] },
  upper: { label: 'Uppercase', fields: [col('column', 'Column')] },
  lower: { label: 'Lowercase', fields: [col('column', 'Column')] },
  trim: { label: 'Trim spaces', fields: [col('column', 'Column')] },
  concat: { label: 'Join text', fields: [{ key: 'parts', label: 'Parts', type: 'operands' }] },
  substring: { label: 'Part of text', fields: [col('column', 'Column'), { key: 'start', label: 'Start', type: 'int', min: 1, max: 10000 }, { key: 'length', label: 'Length', type: 'int', min: 1, max: 10000 }] },
  regex_extract: { label: 'Extract with pattern', fields: [col('column', 'Column'), { key: 'pattern', label: 'Pattern', type: 'text', placeholder: '^([0-9]{5})' }, { key: 'group', label: 'Group', type: 'int', min: 0, max: 9 }] },
};

function defaultArgs(fn, columns) {
  const first = columns[0];
  const args = {};
  for (const f of CATALOG[fn].fields) {
    if (f.type === 'column' && !f.optional) args[f.key] = first;
    else if (f.type === 'choice') args[f.key] = f.options[0];
    else if (f.type === 'int') args[f.key] = f.min;
    else if (f.type === 'text') args[f.key] = '';
    else if (f.type === 'columns') args[f.key] = first ? [first] : [];
    else if (f.type === 'operand' && !f.optional) args[f.key] = { col: first };
    else if (f.type === 'operand_or_today') args[f.key] = { today: true };
    else if (f.type === 'operands') args[f.key] = [{ col: first }, { value: '' }];
    else if (f.type === 'branches') args[f.key] = [{ column: first, op: '=', value: '', then: { value: '' } }];
  }
  return args;
}

export default {
  name: 'WorkflowCalcEditor',
  components: { OperandInput },
  props: {
    modelValue: { type: Object, default: null },
    columns: { type: Array, default: () => [] },
    takenNames: { type: Array, default: () => [] },
    preset: { type: Object, default: null },
  },
  emits: ['save', 'cancel'],
  data() {
    const preset = this.preset || {};
    const fn = CATALOG[preset.fn] ? preset.fn : 'case';
    const cols = preset.column ? [preset.column, ...this.columns.filter((c) => c !== preset.column)] : this.columns;
    const start = this.modelValue
      ? JSON.parse(JSON.stringify(this.modelValue))
      : { name: preset.name || '', fn, args: defaultArgs(fn, cols) };
    return { draft: start, catalog: CATALOG, ops: OPS, error: '' };
  },
  computed: {
    fields() {
      return (CATALOG[this.draft.fn] || { fields: [] }).fields;
    },
  },
  methods: {
    setFn(fn) {
      this.draft = { ...this.draft, fn, args: defaultArgs(fn, this.columns) };
    },
    toggleIn(key, c) {
      const cur = this.draft.args[key] || [];
      this.draft.args[key] = cur.includes(c) ? cur.filter((x) => x !== c) : [...cur, c];
    },
    nullOp(op) { return op === 'IS NULL' || op === 'IS NOT NULL'; },
    listOp(op) { return op === 'IN' || op === 'NOT IN'; },
    newBranch() { return { column: this.columns[0], op: '=', value: '', then: { value: '' } }; },
    displayValue(v) { return Array.isArray(v) ? v.join(', ') : (v == null ? '' : v); },
    parseValue(raw, list) {
      const one = (s) => { const n = Number(s); return s !== '' && !Number.isNaN(n) ? n : s; };
      return list ? raw.split(',').map((s) => s.trim()).filter(Boolean).map(one) : one(raw);
    },
    save() {
      const name = (this.draft.name || '').trim().toUpperCase();
      if (!IDENT.test(name)) { this.error = 'Name must start with a letter and use only letters, digits and underscores.'; return; }
      if (this.takenNames.map((n) => n.toUpperCase()).includes(name) || this.columns.map((c) => c.toUpperCase()).includes(name)) {
        this.error = `${name} is already a column.`; return;
      }
      const args = JSON.parse(JSON.stringify(this.draft.args));
      Object.keys(args).forEach((k) => { if (args[k] === undefined || args[k] === null) delete args[k]; });
      if (args.when) args.when.forEach((b) => { if (this.nullOp(b.op)) delete b.value; });
      this.error = '';
      this.$emit('save', { name, fn: this.draft.fn, args });
    },
  },
};
</script>

<style scoped>
.wce { display: flex; flex-direction: column; gap: 8px; padding: 10px 12px; }
.wce-row { display: flex; align-items: center; gap: 8px; }
.wce-row--top { align-items: flex-start; }
.wce-label { width: 74px; flex-shrink: 0; font-size: 11px; font-weight: 600; color: #6b7280; padding-top: 5px; }
.wce-field { flex: 1; min-width: 0; }
.wce-input { box-sizing: border-box; border: 1px solid #e5e7eb; border-radius: 6px; padding: 3px 7px; font-size: 11.5px; color: #1f2937; background: #fff; min-width: 0; }
.wce-input:focus { outline: none; border-color: var(--color-accent, #0E8C9E); }
.wce-row > .wce-input { flex: 1; }
.wce-mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
.wce-op { width: 84px; flex-shrink: 0; }
.wce-list { display: flex; flex-direction: column; gap: 6px; }
.wce-list-row, .wce-branch-cond, .wce-branch-then { display: flex; align-items: center; gap: 4px; }
.wce-branch { display: flex; flex-direction: column; gap: 4px; padding: 6px; border: 1px solid #f1f2f4; border-radius: 6px; }
.wce-when { font-size: 10.5px; color: #9ca3af; width: 30px; flex-shrink: 0; }
.wce-chips { display: flex; flex-wrap: wrap; gap: 4px 10px; max-height: 96px; overflow-y: auto; }
.wce-chip { display: inline-flex; align-items: center; gap: 4px; font-size: 11px; color: #374151; }
.wce-add { align-self: flex-start; border: none; background: transparent; color: var(--color-accent, #0E8C9E); font-size: 11.5px; font-weight: 600; cursor: pointer; padding: 0; }
.wce-del { border: none; background: transparent; color: #9ca3af; cursor: pointer; font-size: 14px; line-height: 1; }
.wce-del:hover { color: #c23a28; }
.wce-error { margin: 0; font-size: 11.5px; color: #c23a28; }
.wce-actions { display: flex; justify-content: flex-end; gap: 8px; }
.wce-cancel, .wce-save { border-radius: 6px; padding: 4px 12px; font-size: 11.5px; font-weight: 600; cursor: pointer; }
.wce-cancel { border: 1px solid #e5e7eb; background: #fff; color: #4b5563; }
.wce-save { border: 1px solid var(--color-accent, #0E8C9E); background: var(--color-accent, #0E8C9E); color: #fff; }
</style>
